/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './AccountAndFundAlerts.scss';

import { bool, func } from 'prop-types';
import { isNil } from 'lodash';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';

import { ReactComponent as EmailIcon } from 'assets/icons/email.svg';
import LabeledIcon from 'components/shared/LabeledIcon/LabeledIcon';
import FundSelectionModal from 'components/layouts/Preferences/Subscriptions/FundSelectionModal';
import MobileSubscriptionsHeaderRow from 'components/layouts/Preferences/Subscriptions/shared/MobileSubscriptionsHeaderRow';
import SubscriptionsCheckbox from 'components/layouts/Preferences/Subscriptions/shared/SubscriptionsCheckbox';
import SubscriptionsHeaderRow from 'components/layouts/Preferences/Subscriptions/shared/SubscriptionsHeaderRow';
import SubscriptionsRow from 'components/layouts/Preferences/Subscriptions/shared/SubscriptionsRow';
import SubscriptionsRowLabel from 'components/layouts/Preferences/Subscriptions/shared/SubscriptionsRowLabel';
import Radio from 'components/shared/Radio/Radio';
import Content from 'services/Content';

import {
  ALL_FUNDS_SUBSCRIPTION_TYPE_ID,
  DAILY_PRICES_TYPE_ID,
  MARKET_WRAP_UP_IDS,
  MY_FUNDS_SUBSCRIPTION_TYPE_ID,
  MONTHLY_FUND_TYPE_ID,
  SELECTED_FUNDS_SUBSCRIPTION_TYPE_ID
} from 'services/Preferences';

import {
  has529Only,
  isDesktopLayout,
  hasTracOnly
} from 'state/view/ViewSelectors';
import { getHasInvalidOrNoEmailAddress } from 'state/contactMethodMaintenance/ContactMethodMaintenanceSelectors';

class AccountAndFundAlerts extends React.Component {
  state = {
    showDailyFundsModal: false,
    showMonthlyFundsModal: false
  };

  hideModal = (modalName) => () => this.setState({ [modalName]: false });

  showModal = (modalName) => () => this.setState({ [modalName]: true });

  onChange = (key, value) => (checked) => {
    const { onChange } = this.props;
    if (!isNil(checked)) {
      onChange(key, checked);
    } else {
      onChange(key, value);
    }
  };

  onDone = (field) => (fundSelection) => {
    const { onChange } = this.props;
    onChange(field, fundSelection);
  };

  getTaxForms = (subscriptions) => {
    return subscriptions &&
    subscriptions.digitalSubscriptions &&
    subscriptions.digitalSubscriptions.taxForms
      ? subscriptions.digitalSubscriptions.taxForms
      : false;
  };

  getMarketWrapUp = (subscriptions) => {
    if (!subscriptions || !subscriptions.digitalSubscriptions) {
      return false;
    }
    const {
      digitalSubscriptions: { newsLetterIds }
    } = subscriptions || {};
    return (
      newsLetterIds &&
      newsLetterIds.some((newsId) =>
        MARKET_WRAP_UP_IDS.some((wrapUpId) => newsId === wrapUpId)
      )
    );
  };

  getSelectDailyFundPrices = (subscriptions) => {
    if (
      !subscriptions ||
      !subscriptions.digitalSubscriptions ||
      !subscriptions.digitalSubscriptions.priceSubscriptions
    ) {
      return false;
    }
    const {
      digitalSubscriptions: {
        newsLetterIds,
        priceSubscriptions: { typeId, trpCodes }
      }
    } = subscriptions;
    if (
      (trpCodes && trpCodes.length === 0 && typeId === -1) ||
      !newsLetterIds.some((id) => id === DAILY_PRICES_TYPE_ID)
    ) {
      return false;
    }
    return typeId === SELECTED_FUNDS_SUBSCRIPTION_TYPE_ID;
  };

  getMonthlyPriceFunds = (subscriptions) => {
    if (
      !subscriptions ||
      !subscriptions.digitalSubscriptions ||
      !subscriptions.digitalSubscriptions.performanceSubscriptions
    ) {
      return {
        broadAssetClassIds: [],
        trpCodes: []
      };
    }
    const {
      digitalSubscriptions: {
        performanceSubscriptions: { broadAssetClassIds, trpCodes }
      }
    } = subscriptions;
    return {
      broadAssetClassIds,
      trpCodes
    };
  };

  getMyFundsDailyPrices = (subscriptions) => {
    if (
      !subscriptions ||
      !subscriptions.digitalSubscriptions ||
      !subscriptions.digitalSubscriptions.priceSubscriptions
    ) {
      return false;
    }
    const {
      digitalSubscriptions: {
        newsLetterIds,
        priceSubscriptions: { typeId, trpCodes }
      }
    } = subscriptions;
    if (
      (trpCodes && trpCodes.length === 0 && typeId === -1) ||
      !newsLetterIds.some((id) => id === DAILY_PRICES_TYPE_ID)
    ) {
      return false;
    }
    return typeId === MY_FUNDS_SUBSCRIPTION_TYPE_ID;
  };

  getAllFundDailyPrices = (subscriptions) => {
    if (
      !subscriptions ||
      !subscriptions.digitalSubscriptions ||
      !subscriptions.digitalSubscriptions.priceSubscriptions
    ) {
      return false;
    }
    const {
      digitalSubscriptions: {
        newsLetterIds,
        priceSubscriptions: { typeId, trpCodes }
      }
    } = subscriptions;
    if (
      (trpCodes && trpCodes.length === 0 && typeId === -1) ||
      !newsLetterIds.some((id) => id === DAILY_PRICES_TYPE_ID)
    ) {
      return false;
    }
    return typeId === ALL_FUNDS_SUBSCRIPTION_TYPE_ID;
  };

  getMyMonthlyFundPerformances = (subscriptions) => {
    if (
      !subscriptions ||
      !subscriptions.digitalSubscriptions ||
      !subscriptions.digitalSubscriptions.performanceSubscriptions
    ) {
      return false;
    }
    const {
      digitalSubscriptions: {
        newsLetterIds,
        performanceSubscriptions: { typeId, trpCodes }
      }
    } = subscriptions;
    if (
      (trpCodes && trpCodes.length === 0 && typeId === -1) ||
      !newsLetterIds.some((id) => id === MONTHLY_FUND_TYPE_ID)
    ) {
      return false;
    }
    return typeId === MY_FUNDS_SUBSCRIPTION_TYPE_ID;
  };

  getSelectMonthlyFundPrices = (subscriptions) => {
    if (
      !subscriptions ||
      !subscriptions.digitalSubscriptions ||
      !subscriptions.digitalSubscriptions.performanceSubscriptions
    ) {
      return false;
    }
    const {
      digitalSubscriptions: {
        newsLetterIds,
        performanceSubscriptions: { typeId, trpCodes }
      }
    } = subscriptions;

    if (
      (trpCodes && trpCodes.length === 0 && typeId === -1) ||
      !newsLetterIds.some((id) => id === MONTHLY_FUND_TYPE_ID)
    ) {
      return false;
    }
    return typeId === SELECTED_FUNDS_SUBSCRIPTION_TYPE_ID;
  };

  getAllMonthlyFundPrices = (subscriptions) => {
    if (
      !subscriptions ||
      !subscriptions.digitalSubscriptions ||
      !subscriptions.digitalSubscriptions.performanceSubscriptions ||
      subscriptions.digitalSubscriptions.newsLetterIds.length === 0
    ) {
      return false;
    }
    const {
      digitalSubscriptions: {
        newsLetterIds,
        performanceSubscriptions: { typeId, trpCodes }
      }
    } = subscriptions;

    if (
      (trpCodes && trpCodes.length === 0 && typeId === -1) ||
      !newsLetterIds.some((id) => id === MONTHLY_FUND_TYPE_ID)
    ) {
      return false;
    }
    return typeId === ALL_FUNDS_SUBSCRIPTION_TYPE_ID;
  };

  getDailyFundPricesSelected = (subscriptions) => {
    if (
      !subscriptions.digitalSubscriptions ||
      !subscriptions.digitalSubscriptions.priceSubscriptions ||
      subscriptions.digitalSubscriptions.newsLetterIds.length === 0
    ) {
      return false;
    }
    const {
      digitalSubscriptions: {
        newsLetterIds,
        priceSubscriptions: { typeId, trpCodes }
      }
    } = subscriptions;

    if (
      (trpCodes && trpCodes.length === 0 && typeId === -1) ||
      !newsLetterIds.some((id) => id === DAILY_PRICES_TYPE_ID)
    ) {
      return false;
    }

    return (
      typeId === MY_FUNDS_SUBSCRIPTION_TYPE_ID ||
      typeId === SELECTED_FUNDS_SUBSCRIPTION_TYPE_ID ||
      typeId === ALL_FUNDS_SUBSCRIPTION_TYPE_ID
    );
  };

  getPerformanceFundPricesSelected = (subscriptions) => {
    if (
      !subscriptions ||
      !subscriptions.digitalSubscriptions ||
      !subscriptions.digitalSubscriptions.performanceSubscriptions ||
      subscriptions.digitalSubscriptions.newsLetterIds.length === 0
    ) {
      return false;
    }
    const {
      digitalSubscriptions: {
        newsLetterIds,
        performanceSubscriptions: { typeId, trpCodes }
      }
    } = subscriptions;

    if (
      (trpCodes && trpCodes.length === 0 && typeId === -1) ||
      !newsLetterIds.some((id) => id === MONTHLY_FUND_TYPE_ID)
    ) {
      return false;
    }
    return (
      typeId === MY_FUNDS_SUBSCRIPTION_TYPE_ID ||
      typeId === SELECTED_FUNDS_SUBSCRIPTION_TYPE_ID ||
      typeId === ALL_FUNDS_SUBSCRIPTION_TYPE_ID
    );
  };

  getSelectedDailyFunds = (subscriptions) => {
    if (
      !subscriptions ||
      !subscriptions.digitalSubscriptions ||
      !subscriptions.digitalSubscriptions.priceSubscriptions
    ) {
      return {
        broadAssetClassIds: [],
        trpCodes: []
      };
    }
    const {
      digitalSubscriptions: {
        priceSubscriptions: { broadAssetClassIds, trpCodes }
      }
    } = subscriptions;
    return {
      broadAssetClassIds,
      trpCodes
    };
  };

  render() {
    const {
      has529Only,
      hasInvalidOrMissingEmail,
      isDesktopLayout,
      subscriptions,
      hasTracOnly
    } = this.props;

    const taxForms = this.getTaxForms(subscriptions);
    const marketWrapUp = this.getMarketWrapUp(subscriptions);
    //daily
    const dailyFundPricesSelected =
      this.getDailyFundPricesSelected(subscriptions);
    const myFundsDailyPrices = this.getMyFundsDailyPrices(subscriptions);
    const allFundDailyPrices = this.getAllFundDailyPrices(subscriptions);
    const selectDailyFunds = this.getSelectedDailyFunds(subscriptions);
    const selectDailyFundPrices = this.getSelectDailyFundPrices(subscriptions);

    // monthly
    const monthlyPriceFunds = this.getMonthlyPriceFunds(subscriptions);
    const monthlyFundPerformancesSelected =
      this.getPerformanceFundPricesSelected(subscriptions);
    const allMonthlyPerformancePrices =
      this.getAllMonthlyFundPrices(subscriptions);
    const myMonthlyPerformancePrices =
      this.getMyMonthlyFundPerformances(subscriptions);
    const selectMonthlyFundPrices =
      this.getSelectMonthlyFundPrices(subscriptions);

    const { showDailyFundsModal, showMonthlyFundsModal } = this.state;

    return (
      <div className="account-and-fund-alerts">
        {showDailyFundsModal && (
          <FundSelectionModal
            fundsSelected={selectDailyFunds}
            onClose={this.hideModal('showDailyFundsModal')}
            onDone={this.onDone('dailyFundPricesSelected')}
            subscriptions={subscriptions}
            title="Daily Fund Price Alerts: Select Funds"
          />
        )}
        {showMonthlyFundsModal && (
          <FundSelectionModal
            fundsSelected={monthlyPriceFunds}
            onClose={this.hideModal('showMonthlyFundsModal')}
            onDone={this.onDone('monthlyFundPerformanceSelected')}
            subscriptions={subscriptions}
            title="Monthly Fund Performance Alerts: Select Funds"
          />
        )}
        {isDesktopLayout ? (
          <SubscriptionsHeaderRow header="Account &amp; Investment Alerts">
            <LabeledIcon icon={EmailIcon} label="Subscribed" />
          </SubscriptionsHeaderRow>
        ) : (
          <MobileSubscriptionsHeaderRow header="Account &amp; Investment Alerts">
            <div>Communication Type</div>
            <div>Subscribed</div>
          </MobileSubscriptionsHeaderRow>
        )}

        <SubscriptionsRow>
          <SubscriptionsRowLabel
            label="Tax Forms"
            modalContent={Content.subscriptionsTaxForms}
          />
          <SubscriptionsCheckbox
            checked={taxForms}
            name="tax-forms"
            disabled={hasInvalidOrMissingEmail}
            onChange={this.onChange('taxForms')}
          />
        </SubscriptionsRow>
        {!has529Only && (
          <Fragment>
            <SubscriptionsRow>
              <SubscriptionsRowLabel
                label="Market Reviews"
                modalContent={Content.subscriptionsMarketWrapup}
              />
              <SubscriptionsCheckbox
                checked={marketWrapUp}
                name="market-wrap-ups"
                disabled={hasInvalidOrMissingEmail}
                onChange={this.onChange('marketWrapups')}
              />
            </SubscriptionsRow>
            <SubscriptionsRow withSecondaryContainer={true}>
              <SubscriptionsRowLabel
                label="T. Rowe Price Daily Fund Prices"
                modalContent={Content.subscriptionsTrpDailyFundPrices}>
                {dailyFundPricesSelected && (
                  <div className="account-and-fund-alerts-secondary-container">
                    {!hasTracOnly && (
                      <label
                        className="account-and-fund-alerts-secondary-label"
                        htmlFor="daily-fund-prices-my-mutual-funds">
                        <Radio
                          checked={myFundsDailyPrices}
                          disabled={hasInvalidOrMissingEmail}
                          name="daily-fund-prices-my-mutual-funds"
                          onChange={this.onChange(
                            'myFundsDailyPrices',
                            MY_FUNDS_SUBSCRIPTION_TYPE_ID
                          )}
                        />
                        My Mutual Funds
                      </label>
                    )}
                    <label
                      className="account-and-fund-alerts-secondary-label"
                      htmlFor="daily-fund-prices-all-mutual-funds">
                      <Radio
                        checked={allFundDailyPrices}
                        disabled={hasInvalidOrMissingEmail}
                        name="daily-fund-prices-all-mutual-funds"
                        onChange={this.onChange(
                          'allFundsDailyPrices',
                          ALL_FUNDS_SUBSCRIPTION_TYPE_ID
                        )}
                      />
                      All Mutual Funds
                    </label>
                    <label
                      className="account-and-fund-alerts-secondary-label"
                      htmlFor="daily-fund-prices-selected-mutual-funds">
                      <Radio
                        checked={selectDailyFundPrices}
                        disabled={hasInvalidOrMissingEmail}
                        name="daily-fund-prices-selected-mutual-funds"
                        onChange={this.onChange(
                          'selectDailyFundPrices',
                          SELECTED_FUNDS_SUBSCRIPTION_TYPE_ID
                        )}
                      />
                      Selected Mutual Funds{' '}
                      {selectDailyFundPrices && (
                        <span
                          className="edit-selections"
                          onClick={this.showModal('showDailyFundsModal')}>
                          Edit Selections
                        </span>
                      )}
                    </label>
                  </div>
                )}
              </SubscriptionsRowLabel>
              <SubscriptionsCheckbox
                checked={dailyFundPricesSelected}
                name="t-rowe-price-daily-fund-prices"
                disabled={hasInvalidOrMissingEmail}
                onChange={this.onChange('dailyFundPricesSelected')}
              />
            </SubscriptionsRow>
            <SubscriptionsRow withSecondaryContainer={true}>
              <SubscriptionsRowLabel
                label="T. Rowe Price Monthly Fund Performance"
                modalContent={Content.subscriptionsTrpMonthlyFundPerformances}>
                {monthlyFundPerformancesSelected && (
                  <div className="account-and-fund-alerts-secondary-container">
                    {!hasTracOnly && (
                      <label
                        className="account-and-fund-alerts-secondary-label"
                        htmlFor="monthly-fund-performances-my-mutual-funds">
                        <Radio
                          checked={myMonthlyPerformancePrices}
                          disabled={hasInvalidOrMissingEmail}
                          name="monthly-fund-performances-my-mutual-funds"
                          onChange={this.onChange(
                            'myFundsMonthlyFundPerformance',
                            MY_FUNDS_SUBSCRIPTION_TYPE_ID
                          )}
                        />
                        My Mutual Funds
                      </label>
                    )}
                    <label
                      className="account-and-fund-alerts-secondary-label"
                      htmlFor="monthly-fund-performances-all-mutual-funds">
                      <Radio
                        checked={allMonthlyPerformancePrices}
                        disabled={hasInvalidOrMissingEmail}
                        name="monthly-fund-performances-all-mutual-funds"
                        onChange={this.onChange(
                          'allFundsMonthlyFundPerformance',
                          ALL_FUNDS_SUBSCRIPTION_TYPE_ID
                        )}
                      />
                      All Mutual Funds
                    </label>
                    <label
                      className="account-and-fund-alerts-secondary-label"
                      htmlFor="monthly-fund-performances-selected-mutual-funds">
                      <Radio
                        checked={selectMonthlyFundPrices}
                        disabled={hasInvalidOrMissingEmail}
                        name="monthly-fund-performances-selected-mutual-funds"
                        onChange={this.onChange(
                          'selectMonthlyFundPerformance',
                          SELECTED_FUNDS_SUBSCRIPTION_TYPE_ID
                        )}
                      />
                      Selected Mutual Funds{' '}
                      {selectMonthlyFundPrices && (
                        <span
                          className="edit-selections"
                          onClick={this.showModal('showMonthlyFundsModal')}>
                          Edit Selections
                        </span>
                      )}
                    </label>
                  </div>
                )}
              </SubscriptionsRowLabel>
              <SubscriptionsCheckbox
                checked={monthlyFundPerformancesSelected}
                name="t-rowe-price-monthly-fund-performances"
                disabled={hasInvalidOrMissingEmail}
                onChange={this.onChange('monthlyFundPerformancesSelected')}
              />
            </SubscriptionsRow>
          </Fragment>
        )}
      </div>
    );
  }
}

AccountAndFundAlerts.propTypes = {
  has529Only: bool.isRequired,
  isDesktopLayout: bool.isRequired,
  onChange: func.isRequired,
  onFundSelectionChange: func.isRequired,
  hasInvalidOrMissingEmail: bool.isRequired
};

AccountAndFundAlerts.defaultProps = {
  has529Only: false,
  hasInvalidOrMissingEmail: false
};

const mapStateToProps = (state) => ({
  has529Only: has529Only(state),
  isDesktopLayout: isDesktopLayout(state),
  hasInvalidOrMissingEmail: getHasInvalidOrNoEmailAddress(state),
  hasTracOnly: hasTracOnly(state)
});

export default connect(mapStateToProps)(AccountAndFundAlerts);
